/**
 * ViewportPass drawing all active screen-space UI canvases at RenderStage.UI (850) — onto the
 * resolved LDR backbuffer, under editor overlays (900). Injected into renderers via
 * `renderer.addPass(new UIPass(...))` by the UI subsystem (core can't depend on @engine/ui).
 *
 * GPU resources are lazy-created on first execute — external passes can be added to
 * already-initialized pipelines.
 */
import { RenderStage, ViewportPass, type ViewportPassContext } from '@engine/core';
import type { UIDocument } from '../tree/UIDocument';
import { paintDocument } from './paint';
import { screenOrtho } from './scaler';
import { UIBatch, type UIBatchOptions } from './UIBatch';

/** One mounted canvas, as the render pass sees it. Owned/updated by the UI subsystem. */
export interface UICanvasView {
  document: UIDocument;
  /** logical → physical multiplier for this frame (scaler output; subsystem keeps it fresh). */
  scale: number;
  sortOrder: number;
  visible: boolean;
  pixelPerfect: boolean;
  /** Rect-edge antialiasing (canvas checkbox; default on). Text keeps its own MSDF AA. */
  antialias?: boolean;
}

export class UIPass extends ViewportPass {
  readonly stage = RenderStage.UI;
  readonly profileName = 'Render.UI';

  private batch: UIBatch | null = null;
  private readonly vp = new Float32Array(16);

  constructor(
    private readonly getViews: () => readonly UICanvasView[],
    private readonly batchOptions?: UIBatchOptions,
  ) {
    super();
  }

  execute(c: ViewportPassContext): void {
    const views = this.getViews()
      .filter((v) => v.visible)
      .sort((a, b) => a.sortOrder - b.sortOrder);
    if (!views.length) return;

    // Bind the LDR BACKBUFFER explicitly — NOT beginColorPass(): with an HDR camera the context's
    // colorTarget is still the rgba16float scene target even after PostProcessPass resolved to the
    // backbuffer, and our bgra8unorm pipelines would be incompatible (live-verify finding: Dawn
    // "attachment state ... RGBA16Float" errors). Same idiom as the editor's gizmo overlay steps.
    c.ctx.setRenderTarget(null);
    c.ctx.setViewport(0, 0, c.width, c.height);
    c.ctx.disableScissor();
    this.batch ??= new UIBatch(c.ctx, this.batchOptions);
    this.batch.startFrame();
    screenOrtho(this.vp, c.width, c.height);
    this.batch.begin(this.vp, c.width, c.height);
    for (const view of views) {
      this.batch.setAntialias(view.antialias !== false);
      paintDocument(this.batch, view.document, { scale: view.scale, pixelSnap: view.pixelPerfect });
    }
    this.batch.end();
  }

  /** Drop a cached font-atlas texture (font reimported). */
  invalidateFontTexture(fontGuid: string): void {
    this.batch?.invalidateFontTexture(fontGuid);
  }

  /** Drop a UI material's pipelines (shader reimported) — rebuilt from fresh WGSL next frame. */
  invalidateMaterial(materialGuid: string): void {
    this.batch?.invalidateMaterial(materialGuid);
  }

  /** Drop a cached background-image texture (image reimported). */
  invalidateImageTexture(imageGuid: string): void {
    this.batch?.invalidateImageTexture(imageGuid);
  }

  dispose(): void {
    this.batch?.destroy();
    this.batch = null;
  }
}
